import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import { Modal, ModalProps } from '@vimily/bonjoro-ui-components-v2/Modal';
import React, { useMemo, useState } from 'react';
import { useGlobalEvent } from 'beautiful-react-hooks';
import { Anchor } from '@vimily/bonjoro-ui-components-v2/Anchor';

const openSendWithYourDomainEmailModalEventName =
  'send-with-your-domain-email-modal::open';
const closeSendWithYourDomainEmailModalEventName =
  'send-with-your-domain-email-modal::close';

interface SendWithYourDomainEmailContent {
  header?: React.ReactNode;
  description?: React.ReactNode;
  footer?: React.ReactNode;
}

export const openSendWithYourDomainEmailModal = (
  contents?: SendWithYourDomainEmailContent
) =>
  window.dispatchEvent(
    new CustomEvent(openSendWithYourDomainEmailModalEventName, {
      detail: contents,
    })
  );

export const closeSendWithYourDomainEmailModal = () =>
  window.dispatchEvent(
    new CustomEvent(closeSendWithYourDomainEmailModalEventName)
  );

export const SendWithYourDomainEmailModal = () => {
  const [open, setOpen] = useState(false);

  const [header, setHeader] = useState<React.ReactNode>(
    'Add your email to send Roll-ups'
  );
  const [description, setDescription] = useState<React.ReactNode>(
    'To send to more than one recipient you need to send with your business domain email address. Follow the link below to add and verify your email.'
  );
  const [footer, setFooter] = useState<React.ReactNode>(
    <>
      <span>
        Have any questions?&nbsp;
        <Anchor
          href='https://help.bonjoro.com/en/articles/3223569-get-started-with-bonjoro'
          target='_blank'
          rel='noopener noreferrer'
        >
          Read our guide
        </Anchor>
        &nbsp;on the importance of integrating your email.
      </span>
      <span>
        If you are unable to use oAuth or verified domains but still need
        Roll-ups, please contact support by emailing to&nbsp;
        <Anchor
          href='mailto:earth@bonjoro.com'
          target='_blank'
          rel='noopener noreferrer'
        >
          earth@bonjoro.com
        </Anchor>
        &nbsp;explaining the situation. We&apos;d be more than happy to help.
      </span>
    </>
  );

  const onOpenSendWithYourDomainEmailModal = useGlobalEvent<
    CustomEvent<SendWithYourDomainEmailContent>
  >(openSendWithYourDomainEmailModalEventName as keyof WindowEventMap);

  const onCloseSendWithYourDomainEmailModal = useGlobalEvent(
    closeSendWithYourDomainEmailModalEventName as keyof WindowEventMap
  );

  onOpenSendWithYourDomainEmailModal(({ detail }) => {
    setOpen(true);

    if (detail?.header) {
      setHeader(detail?.header);
    }

    if (detail?.description) {
      setDescription(detail?.description);
    }

    if (detail?.footer) {
      setFooter(detail?.footer);
    }
  });

  onCloseSendWithYourDomainEmailModal(() => setOpen(false));

  const modalProps = useMemo<ModalProps>(() => {
    return {
      open,
      onModalClose: () => setOpen(false),
      closeButtonClassName: '!w-5',
      className: '!w-[550px] !inset-px !p-7 rounded-2xl',
    };
  }, [open]);

  return (
    <Modal {...modalProps}>
      <div className='flex flex-col gap-y-[30px] items-center v2-text-neutral-900'>
        <span className='text-[21px] font-bold'>{header}</span>
        <img
          src='https://cdn.bonjoro.com/assets/images/roll-up-bear.png'
          className='w-[275px] h-[212px]'
        />
        <span className='text-base text-center'>{description}</span>
        <Anchor href='/settings/email-setup'>
          <Button variant={ButtonVariant.Primary} className='py-1.5 text-base'>
            Add email address
          </Button>
        </Anchor>
        <div className='flex flex-col gap-y-5 p-5 v2-bg-yellow-50 text-center rounded-md'>
          {footer}
        </div>
      </div>
    </Modal>
  );
};
